// §7.2 — the contract between the OPFS store and its write worker.
//
// Three of the OPFS surfaces this module needs are not in TypeScript's DOM lib:
// `createSyncAccessHandle` is worker-only, `FileSystemSyncAccessHandle` has no declaration at
// all, and `move()` is a File System Access draft addition Chromium ships for *files*. They are
// declared here as narrow structural interfaces rather than reached for through `any` — the same
// approach the sound service takes with WebAudio.

/** The OPFS directory the store writes temp files into. Never part of the §7.1 tree. */
export const TEMP_DIR = '.hangar-tmp';

/** `FileSystemSyncAccessHandle` — synchronous, and only obtainable inside a worker. */
export interface SyncAccessHandle {
  write(buffer: ArrayBufferView, options?: { at?: number }): number;
  truncate(size: number): void;
  flush(): void;
  close(): void;
}

/** The worker-only half of `FileSystemFileHandle`. */
export interface SyncCapableFileHandle {
  createSyncAccessHandle?: () => Promise<SyncAccessHandle>;
}

/** The draft `move()`. Present in Chromium for file handles, absent in Firefox and Safari. */
export interface MovableFileHandle {
  move?: (destination: FileSystemDirectoryHandle, name: string) => Promise<void>;
}

/** Does this realm have synchronous access handles? Worker-only; false on the main thread. */
export function hasSyncAccess(): boolean {
  const ctor = (globalThis as { FileSystemFileHandle?: { prototype?: SyncCapableFileHandle } })
    .FileSystemFileHandle;
  return typeof ctor?.prototype?.createSyncAccessHandle === 'function';
}

/** Does this realm have `FileSystemFileHandle.move()` — i.e. can a write commit by rename? */
export function hasRename(): boolean {
  const ctor = (globalThis as { FileSystemFileHandle?: { prototype?: MovableFileHandle } })
    .FileSystemFileHandle;
  return typeof ctor?.prototype?.move === 'function';
}

export interface WriteTempRequest {
  kind: 'writeTemp';
  id: number;
  /** A name inside `TEMP_DIR`, never a path — the worker never walks the tree. */
  tempName: string;
  /** Transferred, so the store hands over a copy and keeps its own bytes intact. */
  data: ArrayBuffer;
}

export type WorkerRequest = WriteTempRequest;

export interface WorkerReady {
  kind: 'ready';
  syncAccess: boolean;
}

export interface WriteTempOk {
  kind: 'writeTemp:ok';
  id: number;
  bytes: number;
}

export interface WriteTempFail {
  kind: 'writeTemp:fail';
  id: number;
  message: string;
}

export type WorkerMessage = WorkerReady | WriteTempOk | WriteTempFail;
